import type { Condition } from '@brickloop/core/schemas';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, type OrderBlock, type OrderLine, type OrderStatus, type SetUpItemsResponse } from '../api';
import { statusLabel, statusTone } from '../lib/labels';
import { ExistingBinAlert } from './ExistingBinAlert';
import { useLocations } from '../queries';
import { Badge, Button, Card, ErrorMessage, Field, Input, Notice, PageHeader, Select, Stat, Textarea, cn, money, number, plural, unitMoney, useConfirm, useLeaveGuard, when } from '../ui';
import { ColorSwatch, ItemImage } from './ItemImage';

export interface OrderPurchaseRow {
  id: number;
  purchasedOn: string;
  description: string;
  sellerName: string | null;
  supplierId: number | null;
  sellerContact: string | null;
  currency: string;
  vatScheme: 'none' | 'standard' | 'margin';
  status: OrderStatus;
  source: 'manual' | 'bricklink' | 'partout';
  externalId: string | null;
  shippingCost: string;
  insuranceCost: string;
  otherCharges: string;
  taxAmount: string;
  discountAmount: string;
  totalCost: string;
  exchangeRate: string | null;
  trackingNo: string | null;
  trackingLink: string | null;
  shippedOn: string | null;
  receivedOn: string | null;
  sourceStatus: string | null;
  notes: string | null;
  linesEdited: boolean;
}

export interface OrderData {
  purchase: OrderPurchaseRow;
  order: OrderBlock;
}

const today = () => new Date().toISOString().slice(0, 10);
const COND: Record<Condition, string> = { N: 'New', U: 'Used' };

/**
 * A purchase order: the lines expected, what each will cost once postage is spread over it, and the item receipts
 * made against it. Receiving is what puts stock in; nothing else on this screen changes inventory.
 */
export function OrderDetail({ data }: { data: OrderData }) {
  const { purchase: p, order } = data;
  const qc = useQueryClient();
  const locations = useLocations();
  // The inbound shipment page links here to count a parcel in.
  const [receiving, setReceiving] = useState(() => new URLSearchParams(window.location.search).get('receive') === '1');
  const [done, setDone] = useState<string | null>(null);
  const open = p.status === 'ordered' || p.status === 'in_transit' || p.status === 'partially_received';
  const base = order.currency.defaultCode;
  const partout = p.source === 'partout';
  // A part-out is counted in rather than delivered, so it has its own words for where it is up to.
  const stateWord = partout ? ({ ordered: 'To count', partially_received: 'Part counted', received: 'Counted' } as Record<string, string>)[p.status] ?? statusLabel(p.status) : statusLabel(p.status);

  const refresh = () => {
    for (const k of ['purchase', 'purchases', 'dashboard', 'items', 'item', 'lots', 'locations']) void qc.invalidateQueries({ queryKey: [k] });
  };

  const action = useMutation({
    mutationFn: (a: 'mark_in_transit' | 'close' | 'cancel') => api(`/api/v1/purchase-orders/${p.id}/status`, { body: { action: a } }),
    onSuccess: refresh,
  });
  const reverse = useMutation({
    mutationFn: (receiptId: number) => api(`/api/v1/item-receipts/${receiptId}/reverse`, { method: 'POST' }),
    onSuccess: refresh,
  });
  const [shipmentDate, setShipmentDate] = useState('');
  const [shipmentShipDate, setShipmentShipDate] = useState('');
  const [shipmentCarrier, setShipmentCarrier] = useState('');
  const [shipmentTracking, setShipmentTracking] = useState(p.trackingNo ?? '');
  const shipment = useMutation({ mutationFn: () => api('/api/v1/inbound-shipments', { body: { reference: p.description || `Purchase ${p.id}`, expectedShipOn: shipmentShipDate || null, expectedReceiptOn: shipmentDate, carrier: shipmentCarrier || null, trackingNo: shipmentTracking || null, notes: null, purchaseIds: [p.id] } }), onSuccess: refresh });
  const { confirm, dialog } = useConfirm();
  const confirmThen = (message: string, a: 'close' | 'cancel') => async () => (await confirm(message, { label: a === 'cancel' ? 'Cancel it' : 'Finish it', danger: a === 'cancel' })) && action.mutate(a);

  return (
    <>
      {dialog}
      <PageHeader
        title={p.description || `Order ${p.id}`}
        subtitle={
          <>
            <Link to="/purchases" className="hover:underline">
              Purchases
            </Link>{' '}
            / {partout ? 'part-out dated' : 'ordered'} {p.purchasedOn}
            {p.sellerName && (
              <>
                {' '}from{' '}
                {p.supplierId ? (
                  <Link to={`/suppliers/${p.supplierId}`} className="underline">
                    {p.sellerName}
                  </Link>
                ) : (
                  p.sellerName
                )}
              </>
            )}
            {p.source === 'bricklink' && p.externalId && (
              <>
                {' '}
                ·{' '}
                <a className="underline" href={`https://www.bricklink.com/orderDetail.asp?ID=${encodeURIComponent(p.externalId)}`} target="_blank" rel="noreferrer noopener">
                  order {p.externalId} on BrickLink
                </a>
              </>
            )}
          </>
        }
        actions={
          <>
            <Link className="inline-flex items-center rounded-md bg-white px-3.5 py-2 text-sm font-medium text-slate-800 ring-1 ring-slate-300 hover:bg-slate-50" to={`/purchases/${p.id}/document/receipt`} target="_blank">Purchase receipt</Link>
            <Link className="inline-flex items-center rounded-md bg-white px-3.5 py-2 text-sm font-medium text-slate-800 ring-1 ring-slate-300 hover:bg-slate-50" to={`/purchases/${p.id}/document/count`} target="_blank">Count sheet</Link>
            <Badge tone={statusTone(p.status)}>{stateWord}</Badge>
            {p.status === 'ordered' && !partout && (
              <Button variant="secondary" disabled={action.isPending} onClick={() => action.mutate('mark_in_transit')}>
                Mark in transit
              </Button>
            )}
            {open && order.totals.outstanding > 0 && <div className="flex flex-wrap items-end gap-2"><Field label="Expected dispatch"><Input type="date" value={shipmentShipDate} onChange={(e) => setShipmentShipDate(e.target.value)} /></Field><Field label="Expected receipt"><Input type="date" value={shipmentDate} onChange={(e) => setShipmentDate(e.target.value)} /></Field><Field label="Carrier"><Input value={shipmentCarrier} onChange={(e) => setShipmentCarrier(e.target.value)} /></Field><Field label="Tracking number"><Input value={shipmentTracking} onChange={(e) => setShipmentTracking(e.target.value)} /></Field><Button variant="secondary" disabled={!shipmentDate || shipment.isPending} onClick={() => shipment.mutate()}>{shipment.isPending ? 'Creating…' : 'Create inbound shipment'}</Button></div>}
            {open && order.totals.outstanding > 0 && (
              <Button onClick={() => setReceiving((v) => !v)}>{receiving ? (partout ? 'Stop counting' : 'Cancel receipt') : partout ? 'Count parts in' : 'Receive goods'}</Button>
            )}
            {(p.status === 'ordered' || p.status === 'in_transit') && !order.locked && (
              <Button variant="ghost" disabled={action.isPending} onClick={confirmThen(partout ? 'Cancel this part-out? Nothing has been counted, so the sets go back into stock.' : 'Cancel this order? Nothing has arrived, so nothing will be put into stock.', 'cancel')}>
                {partout ? 'Cancel part-out' : 'Cancel order'}
              </Button>
            )}
            {p.status === 'partially_received' && (
              <Button variant="ghost" disabled={action.isPending} onClick={confirmThen(partout ? 'Finish this part-out? Parts not counted in are treated as missing, and the sets\' cost is shared over the parts that were.' : 'Close this order? What has not arrived will stop counting as on order.', 'close')}>
                {partout ? 'Finish, the rest is missing' : 'Close short'}
              </Button>
            )}
          </>
        }
      />
      <div className="space-y-4">
        <ErrorMessage error={action.error} />
        <ErrorMessage error={shipment.error} />
        {done && (
          <Notice tone="green">
            {done}{' '}
            <Link to={`/put-away?purchaseId=${p.id}`} className="font-medium underline">
              Put the stock away
            </Link>
          </Notice>
        )}
        {order.bricklinkArrived && open && (
          <Notice>
            BrickLink says this order has arrived. Nothing is in stock until you receive it here, so check the parcel and press <strong>Receive goods</strong>.
          </Notice>
        )}
        {partout && open && (
          <Notice>
            These are the parts expected from breaking up the sets, and the sets are already out of stock. Press <strong>Count parts in</strong> and enter what is really there; anything you leave out can be counted later. When you finish, the sets&apos; cost is shared across what was counted.
          </Notice>
        )}
        {order.basicItemIds.length > 0 && <BasicItems purchaseId={p.id} count={order.basicItemIds.length} onDone={refresh} />}
        {order.currency.rateMissing && open && <RateCard p={p} order={order} onSaved={refresh} />}

        {order.inboundShipments.length > 0 && (
          <Card title="Inbound shipments">
            <ul className="divide-y divide-slate-100">
              {order.inboundShipments.map((s) => (
                <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                  <Link className="font-medium underline" to={`/inbound-shipments/${s.id}`}>{s.reference}</Link>
                  <span className="text-slate-600">Expected {s.expectedReceiptOn} · {s.status.charAt(0).toUpperCase() + s.status.slice(1)}{s.trackingNo ? ` · ${s.trackingNo}` : ''}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat label={partout ? 'Expected' : 'Ordered'} value={number(order.totals.ordered)} note={plural(order.lines.length, 'line')} />
          <Stat label={partout ? 'Counted' : 'Received'} value={number(order.totals.received)} note={order.receipts.length ? plural(order.receipts.length, 'receipt') : 'Nothing yet'} />
          <Stat label={partout ? 'Left to count' : 'Still expected'} value={number(open ? order.totals.outstanding : 0)} note={open ? (partout ? 'Not yet in stock' : 'Counts as on order') : stateWord} />
          <Stat
            label={partout ? 'Cost of the sets' : 'Paid'}
            value={money(p.totalCost, p.currency)}
            note={order.currency.code !== base ? (order.currency.rate ? `${money((Number(p.totalCost) * Number(order.currency.rate)).toFixed(4), base)} at ${Number(order.currency.rate)}` : `in ${order.currency.code}`) : partout ? 'Shared across the parts' : 'Postage included'}
          />
        </div>

        {receiving ? (
          <ReceiveForm
            partout={partout}
            p={p}
            order={order}
            locations={locations.data ?? []}
            onCancel={() => setReceiving(false)}
            onDone={(message) => {
              setReceiving(false);
              setDone(message);
              refresh();
            }}
          />
        ) : (
          <LinesCard p={p} order={order} open={open} locations={locations.data ?? []} onChanged={refresh} />
        )}

        <div className="grid gap-4 lg:grid-cols-2">
          {!partout && <CostCard p={p} order={order} onSaved={refresh} />}
          {!partout && <DeliveryCard p={p} onSaved={refresh} />}
        </div>

        <Card title="History">
          {order.events.length === 0 ? (
            <p className="text-sm text-slate-500">Nothing recorded yet. From now on, changes BrickLink makes to this order, and what you do to it here, are listed.</p>
          ) : (
            <ol className="space-y-2.5 text-sm">
              {order.events.map((e) => (
                <li key={e.id} className="flex gap-3">
                  <span className="mt-1.5 size-2 shrink-0 rounded-full bg-slate-400" aria-hidden="true" />
                  <div>
                    <div>{e.summary}</div>
                    <div className="text-xs text-slate-500">
                      {when(e.at)}
                      {e.by ? ` · ${e.by}` : ' · BrickLoop'}
                      {e.sourceAt && ` · BrickLink says it changed ${when(e.sourceAt)}`}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
          <p className="mt-3 text-xs text-slate-500">BrickLink is checked every six hours, so a change is listed when it was noticed, which can be a few hours after it happened.</p>
        </Card>

        <Card title="Item receipts" actions={order.receipts.length > 0 ? <Link to={`/put-away?purchaseId=${p.id}`} className="text-sm underline">Put the stock away</Link> : undefined}>
          {reverse.error && <ErrorMessage error={reverse.error} />}
          {order.receipts.length === 0 ? (
            <p className="text-sm text-slate-500">Nothing has been received against this order yet. Receiving is what puts the pieces into stock.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {order.receipts.map((r) => (
                <li key={r.id} className="py-2.5 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Link to={`/item-receipts/${r.id}`} className={r.reversedAt ? 'font-medium text-slate-400 line-through hover:underline' : 'font-medium hover:underline'}>
                      {r.batchName} · {r.receivedOn}
                      {r.reference ? ` · ${r.reference}` : ''}
                    </Link>
                    <span className="flex items-center gap-2 text-slate-600">
                      {r.reversedAt ? <Badge tone="slate">Reversed {when(r.reversedAt)}</Badge> : <span>{plural(r.pieces, 'piece')} into stock</span>}
                      {!r.reversedAt && (
                        <Button
                          variant="ghost"
                          small
                          disabled={!r.reversible || reverse.isPending}
                          title={r.reversible ? undefined : 'Some of this stock has been sold, adjusted or used, so the receipt cannot be reversed. Correct the stock entries with an adjustment instead.'}
                          onClick={async () => (await confirm(`Reverse ${r.batchName}? The ${plural(r.pieces, 'piece')} it put into stock are taken back out, and the order goes back to expecting them.`, { label: 'Reverse it', danger: true })) && reverse.mutate(r.id)}
                        >
                          Reverse
                        </Button>
                      )}
                    </span>
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    {r.lines
                      .map((l) => {
                        const line = order.lines.find((x) => x.id === l.purchaseLineId);
                        return line ? `${line.itemNo} ${line.colorName} ×${number(l.qty)} at ${unitMoney(l.unitCost, base)}` : `×${number(l.qty)}`;
                      })
                      .join(' · ')}
                  </div>
                  {r.notes && <div className="mt-0.5 text-xs text-slate-500">{r.notes}</div>}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}

// ---------------------------------------------------------------- the lines

function LinesCard({ p, order, open, locations, onChanged }: { p: OrderPurchaseRow; order: OrderBlock; open: boolean; locations: { id: number; code: string }[]; onChanged: () => void }) {
  const [editing, setEditing] = useState<number | null>(null);
  const [qty, setQty] = useState('');
  const [price, setPrice] = useState('');
  const save = useMutation({
    mutationFn: (line: OrderLine) =>
      api(`/api/v1/purchase-lines/${line.id}`, {
        method: 'PATCH',
        body: { qtyOrdered: Number(qty), ...(order.locked ? {} : { unitPrice: price }) },
      }),
    onSuccess: () => {
      setEditing(null);
      onChanged();
    },
  });
  const base = order.currency.defaultCode;
  const foreign = order.currency.code !== base;
  const locationCode = (id: number | null) => locations.find((l) => l.id === id)?.code ?? null;

  return (
    <Card title="Lines" actions={p.linesEdited && p.source === 'bricklink' ? <Badge tone="amber">Edited here, so BrickLink changes no longer replace it</Badge> : undefined}>
      <div className="overflow-x-auto">
        <table className="data w-full min-w-[60rem] text-sm">
          <thead>
            <tr>
              <th className="min-w-14" />
              <th>Item</th>
              <th>Colour</th>
              <th>Cond.</th>
              <th className="text-right">Ordered</th>
              <th className="text-right">Received</th>
              <th className="text-right">Expected</th>
              <th className="text-right">Price</th>
              <th className="text-right" title="The price plus this piece's share of postage and charges">
                Cost each{foreign ? ` (${base})` : ''}
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {order.lines.map((l) => (
              <tr key={l.id} className={cn(l.outstanding === 0 && 'text-slate-400')}>
                <td className="min-w-14">
                  <ItemImage itemType={l.itemType} itemNo={l.itemNo} colorId={l.colorId} />
                </td>
                <td>
                  <Link to={`/items/${l.itemId}`} className="font-medium hover:underline">
                    {l.itemNo}
                  </Link>
                  <div className="max-w-56 truncate text-xs text-slate-500">{l.itemName}</div>
                </td>
                <td className="whitespace-nowrap">
                  <ColorSwatch rgb={l.colorRgb} name={l.colorName} />
                  {locationCode(l.defaultLocationId) && <div className="text-xs text-slate-500">usually {locationCode(l.defaultLocationId)}</div>}
                </td>
                <td>{COND[l.condition]}</td>
                {editing === l.id ? (
                  <>
                    <td className="text-right">
                      <Input aria-label="Quantity ordered" className="w-20 text-right" inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value)} />
                    </td>
                    <td className="text-right tabular-nums">{number(l.qtyReceived)}</td>
                    <td />
                    <td className="text-right">
                      <Input aria-label="Unit price" className="w-24 text-right" inputMode="decimal" disabled={order.locked} value={price} onChange={(e) => setPrice(e.target.value)} />
                    </td>
                    <td />
                    <td className="text-right whitespace-nowrap">
                      <Button small disabled={save.isPending || !(Number(qty) >= 1)} onClick={() => save.mutate(l)}>
                        Save
                      </Button>{' '}
                      <Button small variant="ghost" onClick={() => setEditing(null)}>
                        Cancel
                      </Button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="text-right tabular-nums">{number(l.qtyOrdered)}</td>
                    <td className="text-right tabular-nums">{number(l.qtyReceived)}</td>
                    <td className="text-right tabular-nums">{open ? number(l.outstanding) : 0}</td>
                    <td className="text-right tabular-nums">{unitMoney(l.unitPrice, p.currency)}</td>
                    <td className="text-right tabular-nums">{l.unitCostBase ? unitMoney(l.unitCostBase, base) : <span className="text-amber-700">needs a rate</span>}</td>
                    <td className="text-right">
                      {open && (
                        <Button
                          small
                          variant="ghost"
                          onClick={() => {
                            setEditing(l.id);
                            setQty(String(l.qtyOrdered));
                            setPrice(String(Number(l.unitPrice)));
                          }}
                        >
                          Edit
                        </Button>
                      )}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 space-y-2">
        <ErrorMessage error={save.error} />
        <p className="text-xs text-slate-500">
          Cost each is the price plus the piece&apos;s share of postage and other charges, spread by value. Count free extras in the receipt instead; BrickLoop will ask you to confirm them and record them at zero cost. If the supplier charged for the extras, correct the quantity here first.
        </p>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------- receiving

function ReceiveForm({
  partout,
  p,
  order,
  locations,
  onCancel,
  onDone,
}: {
  partout: boolean;
  p: OrderPurchaseRow;
  order: OrderBlock;
  locations: { id: number; code: string }[];
  onCancel: () => void;
  onDone: (message: string) => void;
}) {
  const unsorted = order.lines.filter((l) => l.outstanding > 0);
  const [sort, setSort] = useState<{ key: 'item' | 'colour' | 'cond' | 'expected'; dir: 1 | -1 } | null>(null);
  // A normal receipt is a blind count: every box starts empty and the expected figure is not shown. A part-out starts at the expected list.
  const [qty, setQty] = useState<Record<number, string>>(() => (partout ? Object.fromEntries(unsorted.map((l) => [l.id, String(l.outstanding)])) : {}));
  const [loc, setLoc] = useState<Record<number, string>>({});
  // Counting a part-out: only the ticked parts are counted in; the rest stay to count another time.
  const [ticked, setTicked] = useState<Set<number>>(new Set());
  const tick = (id: number, on: boolean) =>
    setTicked((t) => {
      const next = new Set(t);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const [receivedOn, setReceivedOn] = useState(today());
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const base = order.currency.defaultCode;
  const { confirm, dialog } = useConfirm();

  const waiting = sort
    ? [...unsorted].sort((a, b) => {
        const v = (l: typeof a): string | number =>
          sort.key === 'item' ? l.itemNo : sort.key === 'colour' ? l.colorName : sort.key === 'cond' ? l.condition : l.outstanding;
        const x = v(a);
        const y = v(b);
        const c = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), 'en', { numeric: true });
        return (c || a.itemNo.localeCompare(b.itemNo, 'en', { numeric: true }) || a.colorName.localeCompare(b.colorName)) * sort.dir;
      })
    : unsorted;
  const th = (key: NonNullable<typeof sort>['key'], label: string, right = false) => (
    <th className={right ? 'text-right' : undefined} aria-sort={sort?.key === key ? (sort.dir === 1 ? 'ascending' : 'descending') : undefined}>
      <button type="button" className="font-semibold uppercase hover:underline" onClick={() => setSort(sort?.key === key ? { key, dir: sort.dir === 1 ? -1 : 1 } : { key, dir: 1 })}>
        {label}
        {sort?.key === key ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}
      </button>
    </th>
  );

  const guard = useLeaveGuard(
    ticked.size > 0 || Object.keys(loc).length > 0 || unsorted.some((l) => (qty[l.id] ?? '') !== (partout ? String(l.outstanding) : '')),
    partout ? 'You have ticked or counted parts that are not saved yet. If you leave, you will have to tick and count them again.' : 'You have entered quantities that are not saved yet. If you leave, they are lost.',
  );

  const chosen = waiting.map((l) => ({ l, n: partout && !ticked.has(l.id) ? 0 : Number(qty[l.id] ?? 0) }));
  const problem = chosen.find(({ n }) => n < 0 || !Number.isInteger(n));
  const lines = chosen.filter(({ n }) => n > 0);
  const pieces = lines.reduce((s, { n }) => s + n, 0);
  const overages = partout
    ? []
    : chosen.flatMap(({ l, n }) => {
        const qty = Math.max(0, n - l.outstanding);
        return qty === 0 ? [] : [{ line: l, qty }];
      });
  const overagePieces = overages.reduce((sum, overage) => sum + overage.qty, 0);

  const receive = useMutation({
    mutationFn: (confirmFreeOverages: boolean) =>
      api<{ receipt: { pieces: number; lots: number; lotsCreated: number; overagePieces: number; status: OrderStatus } }>(`/api/v1/purchase-orders/${p.id}/receipts`, {
        body: {
          receivedOn,
          reference: reference.trim() || null,
          notes: notes.trim() || null,
          confirmFreeOverages,
          lines: lines.map(({ l, n }) => ({ purchaseLineId: l.id, qty: n, locationId: loc[l.id] ? Number(loc[l.id]) : null })),
        },
      }),
    onSuccess: ({ receipt }) =>
      onDone(
        `Received ${plural(receipt.pieces, 'piece')} into stock${receipt.overagePieces ? `, including ${plural(receipt.overagePieces, 'free extra')} at zero cost` : ''} (${plural(receipt.lots, 'lot')}, ${receipt.lotsCreated} new). ${
          receipt.status === 'received' ? 'The order is now fully received.' : 'The rest is still on order.'
        }`,
      ),
  });
  const submit = async () => {
    if (overages.length > 0) {
      const listed = overages.map(({ line, qty }) => `${line.itemNo} ${line.colorName} +${qty}`).join(', ');
      const accepted = await confirm(
        `${listed}: you counted ${plural(overagePieces, 'piece')} more than ordered. Receive these as free extras? They will be posted to inventory at zero cost and the amount paid on this order will stay unchanged. If the supplier charged for them, cancel and correct the order line instead.`,
        { label: 'Receive free extras' },
      );
      if (!accepted) return;
    }
    receive.mutate(overages.length > 0);
  };

  return (
    <Card
      title={partout ? 'Count parts in' : 'Receive goods'}
      actions={
        <div className="flex gap-2">
          {partout && (
            <Button variant="ghost" onClick={() => setTicked(new Set())}>
              Untick all
            </Button>
          )}
          {partout && (
            <Button
              variant="secondary"
              onClick={() => {
                setQty(Object.fromEntries(waiting.map((l) => [l.id, String(l.outstanding)])));
                setTicked(new Set(waiting.map((l) => l.id)));
              }}
            >
              Tick everything left
            </Button>
          )}
        </div>
      }
    >
      {guard.dialog}
      {dialog}
      <p className="mb-3 text-sm text-slate-600">
        {partout
          ? 'Tick the parts you have checked and enter how many there really are. Only ticked parts are counted in; the rest stay here to count another time.'
          : 'Count the parcel and enter what actually turned up against each part. The expected quantities are hidden on purpose, and a line you leave empty is not received, so you can count some now and the rest later. Each line becomes stock costed at its price plus its share of postage.'}
      </p>
      <div className="overflow-x-auto">
        <table className={cn('data w-full text-sm', partout ? 'min-w-[48rem]' : 'min-w-[40rem]')}>
          <thead>
            <tr>
              {partout && <th className="w-8" />}
              <th className="min-w-14" />
              {th('item', 'Item')}
              {th('colour', 'Colour')}
              {th('cond', 'Cond.')}
              {partout && th('expected', 'Expected', true)}
              <th className="text-right">{partout ? 'Counted' : 'Arrived'}</th>
              <th>Bin</th>
              <th className="text-right">Cost each</th>
            </tr>
          </thead>
          <tbody>
            {waiting.map((l) => {
              const defaultCode = locations.find((x) => x.id === l.defaultLocationId)?.code;
              const n = Number(qty[l.id] ?? 0);
              return (
                <tr key={l.id}>
                  {partout && (
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Count in: ${l.itemNo} ${l.colorName}`}
                        className="size-4"
                        checked={ticked.has(l.id)}
                        onChange={(e) => tick(l.id, e.target.checked)}
                      />
                    </td>
                  )}
                  <td className="min-w-14">
                    <ItemImage itemType={l.itemType} itemNo={l.itemNo} colorId={l.colorId} />
                  </td>
                  <td className="font-medium">{l.itemNo}</td>
                  <td className="whitespace-nowrap">
                    <ColorSwatch rgb={l.colorRgb} name={l.colorName} />
                  </td>
                  <td>{COND[l.condition]}</td>
                  {partout && <td className="text-right tabular-nums">{number(l.outstanding)}</td>}
                  <td className="text-right">
                    <Input
                      aria-label={`Arrived: ${l.itemNo} ${l.colorName}`}
                      placeholder={partout ? undefined : 'count'}
                      className={cn('w-24 text-right', (n < 0 || !Number.isInteger(n)) && 'border-red-400', n > l.outstanding && 'border-amber-400')}
                      inputMode="numeric"
                      value={qty[l.id] ?? ''}
                      onChange={(e) => {
                        setQty({ ...qty, [l.id]: e.target.value });
                        if (partout) tick(l.id, true);
                      }}
                    />
                  </td>
                  <td>
                    <Select aria-label={`Bin: ${l.itemNo} ${l.colorName}`} className="w-36" value={loc[l.id] ?? ''} onChange={(e) => setLoc({ ...loc, [l.id]: e.target.value })}>
                      <option value="">{defaultCode ? `Usual (${defaultCode})` : 'No bin yet'}</option>
                      {locations.map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.code}
                        </option>
                      ))}
                    </Select>
                    {n > 0 && (
                      <div className="mt-1 max-w-xs text-xs">
                        <ExistingBinAlert itemType={l.itemType} itemNo={l.itemNo} colorId={l.colorId} condition={l.condition} chosen={loc[l.id] ?? ''} onUse={(id) => setLoc({ ...loc, [l.id]: String(id) })} />
                      </div>
                    )}
                  </td>
                  <td className="text-right tabular-nums">{l.unitCostBase ? unitMoney(l.unitCostBase, base) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <Field label="Date received">
          <Input type="date" required value={receivedOn} onChange={(e) => setReceivedOn(e.target.value)} />
        </Field>
        <Field label="Reference" hint="A delivery note number, if there is one.">
          <Input value={reference} onChange={(e) => setReference(e.target.value)} />
        </Field>
        <Field label="Notes">
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>
      <div className="mt-4 space-y-2">
        {problem && <Notice tone="amber">{problem.l.itemNo} {problem.l.colorName}: enter a whole number, 0 or more.</Notice>}
        {!partout && overages.length > 0 && (
          <Notice tone="blue">
            {plural(overagePieces, 'piece')} more than ordered will need confirmation before it is put into stock. Confirming records it as a free extra at zero cost and leaves the amount paid unchanged.
          </Notice>
        )}
        {partout && lines.some(({ l, n }) => n > l.outstanding) && (
          <Notice tone="blue">
            {plural(lines.filter(({ l, n }) => n > l.outstanding).length, 'part')} counted more than expected. That is fine: the line grows to match, and when the counting is finished the costs are shared out again so the sets still cost what they did.
          </Notice>
        )}
        <ErrorMessage error={receive.error} />
        <div className="flex gap-2">
          <Button disabled={!!problem || pieces === 0 || receive.isPending || order.currency.rateMissing} onClick={() => void submit()}>
            {receive.isPending ? 'Receiving…' : pieces === 0 ? (partout ? 'Tick the parts to count' : 'Receive') : partout ? `Count ${plural(pieces, 'piece')} in` : `Receive ${plural(pieces, 'piece')} into stock`}
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
        {order.currency.rateMissing && <p className="text-xs text-amber-700">Enter the exchange rate above first.</p>}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------- exchange rate

function RateCard({ p, order, onSaved }: { p: OrderPurchaseRow; order: OrderBlock; onSaved: () => void }) {
  const [rate, setRate] = useState('');
  const save = useMutation({
    mutationFn: () => api(`/api/v1/purchase-orders/${p.id}`, { method: 'PATCH', body: { exchangeRate: Number(rate) } }),
    onSuccess: () => {
      setRate('');
      onSaved();
    },
  });
  const { code, defaultCode } = order.currency;
  return (
    <Notice tone="amber">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-64 flex-1">
          This order is in <strong>{code}</strong>, but stock is costed in <strong>{defaultCode}</strong>. Enter the exchange rate (how many {defaultCode} one {code} is worth) before receiving it.
        </div>
        <Field label={`${defaultCode} per ${code}`} className="w-36">
          <Input inputMode="decimal" placeholder="0.85" value={rate} onChange={(e) => setRate(e.target.value)} />
        </Field>
        <Button disabled={!(Number(rate) > 0) || save.isPending} onClick={() => save.mutate()}>
          Save rate
        </Button>
      </div>
      <div className="mt-2">
        <ErrorMessage error={save.error} />
      </div>
    </Notice>
  );
}

// ---------------------------------------------------------------- the money

function CostCard({ p, order, onSaved }: { p: OrderPurchaseRow; order: OrderBlock; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const c = order.charges;
  const [f, setF] = useState({
    shippingCost: String(Number(p.shippingCost)),
    insuranceCost: String(Number(p.insuranceCost)),
    otherCharges: String(Number(p.otherCharges)),
    taxAmount: String(Number(p.taxAmount)),
    discountAmount: String(Number(p.discountAmount)),
    vatScheme: p.vatScheme,
    currency: p.currency,
    exchangeRate: p.exchangeRate ? String(Number(p.exchangeRate)) : '',
  });
  const save = useMutation({
    mutationFn: () =>
      api(`/api/v1/purchase-orders/${p.id}`, {
        method: 'PATCH',
        body: {
          shippingCost: f.shippingCost || '0',
          insuranceCost: f.insuranceCost || '0',
          otherCharges: f.otherCharges || '0',
          taxAmount: f.taxAmount || '0',
          discountAmount: f.discountAmount || '0',
          vatScheme: f.vatScheme,
          currency: f.currency.trim().toUpperCase(),
          ...(f.exchangeRate.trim() ? { exchangeRate: Number(f.exchangeRate) } : {}),
        },
      }),
    onSuccess: () => {
      setEditing(false);
      onSaved();
    },
  });
  const rows: [string, string, string?][] = [
    ['Items', c.items],
    ['Postage', c.shipping],
    ['Insurance', c.insurance],
    ['Other charges', c.other],
    ['VAT', c.tax, Number(c.recoverableTax) > 0 ? 'claimed back, so not in the cost' : undefined],
    ['Discount', c.discount],
  ];
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  return (
    <Card
      title="What it cost"
      actions={
        order.locked ? (
          <Badge>Fixed once goods are received</Badge>
        ) : (
          <Button variant="secondary" small onClick={() => setEditing((v) => !v)}>
            {editing ? 'Cancel' : 'Edit'}
          </Button>
        )
      }
    >
      {editing ? (
        <form
          className="grid gap-3 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <Field label="Postage">
            <Input inputMode="decimal" value={f.shippingCost} onChange={set('shippingCost')} />
          </Field>
          <Field label="Insurance">
            <Input inputMode="decimal" value={f.insuranceCost} onChange={set('insuranceCost')} />
          </Field>
          <Field label="Other charges">
            <Input inputMode="decimal" value={f.otherCharges} onChange={set('otherCharges')} />
          </Field>
          <Field label="VAT">
            <Input inputMode="decimal" value={f.taxAmount} onChange={set('taxAmount')} />
          </Field>
          <Field label="Discount">
            <Input inputMode="decimal" value={f.discountAmount} onChange={set('discountAmount')} />
          </Field>
          <Field label="VAT scheme" hint="Under the standard scheme the VAT is claimed back, so it is not part of the cost.">
            <Select value={f.vatScheme} onChange={set('vatScheme')}>
              <option value="none">Not VAT-registered</option>
              <option value="standard">Standard rate</option>
              <option value="margin">Second-hand margin scheme</option>
            </Select>
          </Field>
          <Field label="Currency">
            <Input maxLength={3} value={f.currency} onChange={set('currency')} />
          </Field>
          <Field label={`${order.currency.defaultCode} per ${f.currency.toUpperCase() || 'unit'}`} hint="Only for an order in another currency.">
            <Input inputMode="decimal" value={f.exchangeRate} onChange={set('exchangeRate')} />
          </Field>
          <div className="sm:col-span-2">
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
          <div className="sm:col-span-2">
            <ErrorMessage error={save.error} />
          </div>
        </form>
      ) : (
        <dl className="space-y-1.5 text-sm">
          {rows
            .filter(([label, value]) => label === 'Items' || Number(value) !== 0)
            .map(([label, value, note]) => (
              <div key={label} className="flex justify-between gap-3">
                <dt className="text-slate-600">
                  {label}
                  {note && <span className="text-xs text-slate-400"> · {note}</span>}
                </dt>
                <dd className="tabular-nums">
                  {label === 'Discount' ? '−' : ''}
                  {money(value, p.currency)}
                </dd>
              </div>
            ))}
          <div className="flex justify-between gap-3 border-t border-slate-200 pt-1.5 font-semibold">
            <dt>Paid</dt>
            <dd className="tabular-nums">{money(c.total, p.currency)}</dd>
          </div>
          {Number(c.absorbed) > 0 && (
            <p className="pt-1 text-xs text-slate-500">
              {money(c.absorbed, p.currency)} of postage and insurance is absorbed as a running cost, so the parts cost {money((Number(c.total) - Number(c.absorbed) - Number(c.recoverableTax)).toFixed(4), p.currency)} between them. (Change this under Settings.)
            </p>
          )}
          {order.currency.code !== order.currency.defaultCode && (
            <div className="flex justify-between gap-3 text-slate-600">
              <dt>Exchange rate</dt>
              <dd>{order.currency.rate ? `${Number(order.currency.rate)} ${order.currency.defaultCode} per ${order.currency.code}` : 'not set'}</dd>
            </div>
          )}
        </dl>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------- delivery

function DeliveryCard({ p, onSaved }: { p: OrderPurchaseRow; onSaved: () => void }) {
  const [notes, setNotes] = useState(p.notes ?? '');
  const save = useMutation({
    mutationFn: () =>
      api(`/api/v1/purchase-orders/${p.id}`, {
        method: 'PATCH',
        body: { notes: notes.trim() || null },
      }),
    onSuccess: onSaved,
  });
  const dirty = notes !== (p.notes ?? '');
  return (
    <Card title="Purchase notes">
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Field label="Notes">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        <p className="text-xs text-slate-500">
          {p.shippedOn ? `Shipped ${p.shippedOn}. ` : ''}
          {p.receivedOn ? `Last received ${p.receivedOn}. ` : ''}
          {p.source === 'bricklink' && p.sourceStatus ? `BrickLink status: ${p.sourceStatus.toLowerCase()}.` : ''}
        </p>
        <div className="flex items-center gap-3">
          <Button type="submit" variant="secondary" disabled={!dirty || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
        <ErrorMessage error={save.error} />
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------- items that are only basic records

/**
 * Items an order brings in start as basic records: a name and a category. This asks BrickLink for the rest (weight,
 * size, year, alternate numbers) for every one of them, a batch at a time so it can show how far it has got.
 */
function BasicItems({ purchaseId, count, onDone }: { purchaseId: number; count: number; onDone: () => void }) {
  const [progress, setProgress] = useState<{ done: number; total: number; unknown: number } | null>(null);
  const [stopped, setStopped] = useState<string | null>(null);
  const run = useMutation({
    mutationFn: async () => {
      setStopped(null);
      let done = 0;
      let unknown = 0;
      let remaining = count;
      setProgress({ done, total: count, unknown });
      while (remaining > 0) {
        const { result } = await api<SetUpItemsResponse>(`/api/v1/purchase-orders/${purchaseId}/set-up-items`, { method: 'POST', body: {} });
        done += result.processed;
        unknown += result.notOnBricklink;
        remaining = result.remaining;
        setProgress({ done, total: count, unknown });
        if (result.stoppedBecause) {
          setStopped(result.stoppedBecause);
          break;
        }
        if (result.processed === 0) break;
      }
    },
    onSettled: onDone,
  });
  const working = run.isPending;
  return (
    <Notice tone="amber">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-64 flex-1">
          <strong>{plural(count, 'item')} on this order {count === 1 ? 'is' : 'are'} only basic records</strong>: a name and a category, with no weight, size or year.{' '}
          {working && progress ? `Asking BrickLink… ${number(progress.done)} of ${number(progress.total)} done.` : 'Create the item records to ask BrickLink for the rest. It is one read-only request per item.'}
          {stopped && <span className="block text-xs">Stopped early: {stopped} Press the button again to carry on.</span>}
          {!working && progress && progress.unknown > 0 && <span className="block text-xs">{plural(progress.unknown, 'item')} {progress.unknown === 1 ? 'is' : 'are'} not known to BrickLink and keep what the order gave.</span>}
        </div>
        <Button disabled={working} onClick={() => run.mutate()}>
          {working ? 'Creating…' : `Create ${plural(count, 'item record')}`}
        </Button>
      </div>
      <div className="mt-2">
        <ErrorMessage error={run.error} />
      </div>
    </Notice>
  );
}
